/**
 * Administration reads and writes (Phase 4): users, clients, access grants,
 * terminology and blueprint deletion.
 *
 * Every shape here was checked against the running API, not written from the
 * route source alone.
 *
 * Terminology: the stored OVERRIDE (`client.terminology`, `blueprint.terminology`)
 * and the RESOLVED set (`terms`) are different things and the API returns both.
 * The editor reads the override and PATCHes the override. PATCH replaces the
 * stored object wholesale, so reading anything else would silently overwrite it.
 *
 * Nothing here renders a display word. The nine overridable terms are the
 * caller's business, through useTerms().
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Terminology, TerminologyOverride } from '@blueprint/shared';
import { ApiError, api, request } from './api';
import { CURRENT_USER_KEY, type CurrentUser, type GlobalRole } from './auth';
import { blueprintKeys, type Blueprint, type BlueprintRole, type BlueprintSummary } from './blueprints';

/* ------------------------------------------------------------------ shapes */

export interface ManagedUser {
  id: string;
  email: string;
  name: string;
  globalRole: GlobalRole;
  isActive: boolean;
  mustChangePassword: boolean;
  lastLoginAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AdminClient {
  id: string;
  name: string;
  slug: string;
  createdAt: string;
  updatedAt: string;
  /** The client's own stored override — raw, not resolved. `{}` when it has set nothing. */
  terminology: TerminologyOverride;
}

/** Only `editor` and `viewer` are grantable; `admin` is a global role. */
export type GrantRole = Exclude<BlueprintRole, 'admin'>;

export interface Grant {
  userId: string;
  email: string;
  name: string;
  /** A grant held by a deactivated user still exists; it does nothing until they are reactivated. */
  isActive: boolean;
  role: GrantRole;
  grantedBy: string;
  createdAt: string;
}

export interface DeletedBlueprint extends Blueprint {
  deletedAt: string | null;
}

/* -------------------------------------------------------------------- keys */

export const adminKeys = {
  users: ['admin', 'users'] as const,
  clients: ['admin', 'clients'] as const,
  /** Every blueprint an admin can see, archived included. */
  blueprints: ['admin', 'blueprints'] as const,
  archive: ['admin', 'archive'] as const,
  access: (blueprintId: string) => ['admin', 'access', blueprintId] as const,
  accessAll: ['admin', 'access'] as const,
};

/* ------------------------------------------------------------------ errors */

/** A list of the API's own reasons for a refusal, when it sent a list. */
export function detailList(err: unknown, key: string): string[] {
  if (!(err instanceof ApiError)) return [];
  const details = err.body?.error?.details as Record<string, unknown> | undefined;
  const list = details?.[key];
  if (!Array.isArray(list)) return [];
  return list.flatMap((item) => {
    if (typeof item === 'string') return [item];
    if (item && typeof item === 'object' && typeof (item as { message?: unknown }).message === 'string') {
      return [(item as { message: string }).message];
    }
    return [];
  });
}

/** The most useful sentence for a failed write. The API's own wording wins. */
export function errorText(err: unknown, fallback = 'Something went wrong. Try again.'): string {
  if (err instanceof ApiError) {
    if (err.status === 0) return 'Could not reach the server. Check your connection and try again.';
    if (err.status === 429) return 'Too many requests. Wait a moment and try again.';
    return err.message;
  }
  return fallback;
}

/* ------------------------------------------------------------------- users */

export function useUsers() {
  return useQuery({
    queryKey: adminKeys.users,
    queryFn: async ({ signal }) => (await api.get<{ users: ManagedUser[] }>('/users', signal)).users,
  });
}

export function useCreateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { email: string; name: string; password: string; globalRole: GlobalRole }) =>
      api.post<{ user: ManagedUser }>('/users', body).then((r) => r.user),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: adminKeys.users }),
  });
}

export function useUpdateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...patch
    }: { id: string } & Partial<{ name: string; globalRole: GlobalRole; isActive: boolean }>) =>
      api.patch<{ user: ManagedUser }>(`/users/${id}`, patch).then((r) => r.user),
    onSuccess: () =>
      // A role or active flag changes what the access screens should show.
      Promise.all([
        queryClient.invalidateQueries({ queryKey: adminKeys.users }),
        queryClient.invalidateQueries({ queryKey: adminKeys.accessAll }),
      ]),
  });
}

export function useResetPassword() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, newPassword }: { id: string; newPassword: string }) =>
      api.post<{ user: ManagedUser }>(`/users/${id}/reset-password`, { newPassword }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: adminKeys.users }),
  });
}

/** POST /auth/change-password — the caller's own password, which needs the current one. */
export function useChangePassword() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { currentPassword: string; newPassword: string }) =>
      api.post<{ user: CurrentUser }>('/auth/change-password', body).then((r) => r.user),
    onSuccess: (user) => {
      // The API has started a fresh session; the identity it returned has
      // mustChangePassword cleared, which is what lifts the route guard.
      queryClient.setQueryData(CURRENT_USER_KEY, user);
    },
  });
}

/* ----------------------------------------------------------------- clients */

export function useClients() {
  return useQuery({
    queryKey: adminKeys.clients,
    queryFn: async ({ signal }) =>
      (await api.get<{ clients: AdminClient[] }>('/clients', signal)).clients,
  });
}

export function useCreateClient() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; slug: string }) =>
      api.post<{ client: AdminClient }>('/clients', body).then((r) => r.client),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: adminKeys.clients }),
  });
}

/**
 * Rename a client. Sends the name and nothing else: the slug is fixed for the
 * life of the client (the hard-delete confirmation token, reserved while the
 * client is in the archive, and a handle people keep in links). A client's name is
 * copied into every blueprint payload, so those caches go stale too.
 */
export function useRenameClient() {
  const invalidate = useInvalidateBlueprints();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      api.patch<{ client: AdminClient }>(`/clients/${id}`, { name }).then((r) => r.client),
    onSuccess: () =>
      Promise.all([invalidate(), queryClient.invalidateQueries({ queryKey: adminKeys.clients })]),
  });
}

/** Every blueprint the admin can see, archived ones included. Deleted ones are in the archive only. */
export function useAllBlueprints() {
  return useQuery({
    queryKey: adminKeys.blueprints,
    queryFn: async ({ signal }) =>
      (await api.get<{ blueprints: BlueprintSummary[] }>('/blueprints?includeArchived=true', signal))
        .blueprints,
  });
}

export function useArchive() {
  return useQuery({
    queryKey: adminKeys.archive,
    queryFn: async ({ signal }) =>
      (await api.get<{ blueprints: DeletedBlueprint[] }>('/blueprints/deleted', signal)).blueprints,
  });
}

/**
 * Everything a blueprint write can make stale. Returns the promise, so the
 * mutation that calls it stays pending until the screen has fresh data and never
 * flashes the old list between "saved" and the refetch.
 */
function useInvalidateBlueprints() {
  const queryClient = useQueryClient();
  return () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: adminKeys.blueprints }),
      queryClient.invalidateQueries({ queryKey: adminKeys.archive }),
      queryClient.invalidateQueries({ queryKey: adminKeys.accessAll }),
      queryClient.invalidateQueries({ queryKey: blueprintKeys.all }),
    ]);
}

export function useCreateBlueprint() {
  const invalidate = useInvalidateBlueprints();
  return useMutation({
    mutationFn: (body: { clientId: string; name: string; slug: string; description?: string | null }) =>
      api.post<{ blueprint: Blueprint }>('/blueprints', body).then((r) => r.blueprint),
    onSuccess: invalidate,
  });
}

export function useRenameBlueprint() {
  const invalidate = useInvalidateBlueprints();
  return useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      api.patch<{ blueprint: Blueprint }>(`/blueprints/${id}`, { name }).then((r) => r.blueprint),
    onSuccess: invalidate,
  });
}

export function useSoftDeleteBlueprint() {
  const invalidate = useInvalidateBlueprints();
  return useMutation({
    mutationFn: (id: string) => api.delete<{ id: string; mode: 'soft' }>(`/blueprints/${id}`),
    onSuccess: invalidate,
  });
}

export function useRestoreBlueprint() {
  const invalidate = useInvalidateBlueprints();
  return useMutation({
    mutationFn: (id: string) => api.post<{ blueprint: Blueprint }>(`/blueprints/${id}/restore`),
    onSuccess: invalidate,
  });
}

/** Irreversible. `confirm` must be the blueprint's own slug, typed by the person. */
export function useHardDeleteBlueprint() {
  const invalidate = useInvalidateBlueprints();
  return useMutation({
    mutationFn: ({ id, confirm }: { id: string; confirm: string }) =>
      api.delete<{ id: string; mode: 'hard' }>(
        `/blueprints/${id}?hard=true&confirm=${encodeURIComponent(confirm)}`,
      ),
    onSuccess: invalidate,
  });
}

/* ------------------------------------------------------------- terminology */

export function useSetClientTerminology() {
  const invalidate = useInvalidateBlueprints();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, terminology }: { id: string; terminology: TerminologyOverride }) =>
      api.patch<{ client: AdminClient; terms: Terminology }>(`/clients/${id}/terminology`, {
        terminology,
      }),
    onSuccess: () =>
      Promise.all([invalidate(), queryClient.invalidateQueries({ queryKey: adminKeys.clients })]),
  });
}

export function useSetBlueprintTerminology() {
  const invalidate = useInvalidateBlueprints();
  return useMutation({
    mutationFn: ({ id, terminology }: { id: string; terminology: TerminologyOverride }) =>
      api.patch<{ blueprint: Blueprint; terms: Terminology }>(`/blueprints/${id}/terminology`, {
        terminology,
      }),
    onSuccess: invalidate,
  });
}

/* ------------------------------------------------------------------ access */

export function useGrants(blueprintId: string | undefined) {
  return useQuery({
    queryKey: adminKeys.access(blueprintId ?? ''),
    queryFn: async ({ signal }) =>
      (await api.get<{ grants: Grant[] }>(`/blueprints/${blueprintId}/access`, signal)).grants,
    enabled: Boolean(blueprintId),
  });
}

export function useSetGrant() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ blueprintId, userId, role }: { blueprintId: string; userId: string; role: GrantRole }) =>
      request<{ grant: Grant }>(`/blueprints/${blueprintId}/access/${userId}`, { method: 'PUT', body: { role } }),
    onSuccess: (_r, v) => queryClient.invalidateQueries({ queryKey: adminKeys.access(v.blueprintId) }),
  });
}

export function useRevokeGrant() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ blueprintId, userId }: { blueprintId: string; userId: string }) =>
      api.delete<void>(`/blueprints/${blueprintId}/access/${userId}`),
    onSuccess: (_r, v) => queryClient.invalidateQueries({ queryKey: adminKeys.access(v.blueprintId) }),
  });
}
